/**
 * Root Layout
 * Responsive dashboard with collapsible sidebar
 */

import { useEffect, useRef } from 'react'

import {
  createRootRouteWithContext,
  initializeToastService,
  Link,
  Outlet,
  useDispatch,
  useLocation,
  useSelector,
  useTranslation,
} from '@ufc/core'
import { cn, Toaster, toast } from '@ufc/uikit'

import { DashboardHeader, DashboardSidebar } from '@/components'
import i18n from '@/lib/i18n'
import { selectIsSuperAdmin } from '@/lib/auth'
import {
  I18nProvider,
  LanguageSessionProvider,
  SidebarProvider,
  StoreProvider,
  ThemeProvider,
  useSidebar,
} from '@/providers'
import type { ToastOptions } from '@ufc/core'
import type { RouterContext } from '@/main'
import type { TFunction } from 'i18next'

// Auth
import {
  authActions,
  selectIsAuthenticated,
  selectAuthIsInitialized,
  selectCanAccessKioskPortal,
} from '@ufc/domains/auth'


function resolveToastText(value?: string): string | undefined {
  if (!value) return value
  if (!i18n.exists(value)) return value
  return i18n.t(value)
}

function createTranslatedToast(
  toastFn: (options: ToastOptions) => void
): (options: ToastOptions) => void {
  return (options: ToastOptions) =>
    toastFn({
      ...options,
      title: resolveToastText(options.title),
      description: resolveToastText(options.description),
    })
}

// Initialize toast service for saga usage
initializeToastService(createTranslatedToast(toast))

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

const AUTH_ROUTES = ['/login', '/privacy']

const DASHBOARD_FOOTER_LINKS = [
  { to: '/privacy', labelKey: 'privacy.link' },
]

function renderDashboardFooterLinks(t: TFunction): JSX.Element[] {
  return DASHBOARD_FOOTER_LINKS.map((link) => (
    <Link
      key={link.to}
      to={link.to}
      className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      {t(link.labelKey)}
    </Link>
  ))
}

function DashboardLayout() {
  const { isMobile } = useSidebar()
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  const { t } = useTranslation()

  return (
    <div className="relative flex h-screen overflow-hidden">
      {/* Background gradient — single element, no blur filters */}
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/50" />

      {/* Content */}
      <div className="relative flex w-full h-full z-10">
        {/* Only show sidebar for super admin */}
        {isSuperAdmin && <DashboardSidebar />}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 h-full">
          <div className="pt-safe">
            <DashboardHeader />
          </div>
          <main
            className={cn(
              'flex-1 overflow-y-auto w-full',
              // Add padding if sidebar is hidden to ensure content isn't flush against edge
              isMobile ? 'p-4' : 'p-8',
              // For kiosk mode, we want full width/height but with good spacing
              !isSuperAdmin && 'p-6 md:p-10' 
            )}
          >
            <Outlet />
          </main>
          <footer className="px-4 pb-6 md:px-8">
            <div className="flex flex-wrap items-center gap-4">
              {renderDashboardFooterLinks(t)}
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}

/**
 * Auth Initializer
 * Dispatches auth initialization on mount
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(authActions.initializeAuth())
  }, [dispatch])

  return <>{children}</>
}

function FullPageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
    </div>
  )
}

function KioskProtectedRoute({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isInitialized = useSelector(selectAuthIsInitialized)
  const canAccessPortal = useSelector(selectCanAccessKioskPortal)
  const loggedOutRef = useRef(false)

  useEffect(() => {
    if (isInitialized && isAuthenticated && !canAccessPortal && !loggedOutRef.current) {
      loggedOutRef.current = true
      toast({ title: 'Access Denied', description: 'Your role does not have access to the United Feed Company driver app.', variant: 'destructive' })
      dispatch(authActions.logout())
    }
  }, [isInitialized, isAuthenticated, canAccessPortal, dispatch])

  if (!isInitialized) return <FullPageSpinner />
  if (!isAuthenticated) return <FullPageSpinner />
  return <>{children}</>
}

function RootLayout() {
  const location = useLocation()
  const isAuthRoute = AUTH_ROUTES.includes(location.pathname)

  return (
    <StoreProvider>
      <AuthInitializer>
        <ThemeProvider>
          <I18nProvider>
            <LanguageSessionProvider>
              <SidebarProvider>
                {isAuthRoute ? (
                  // Auth pages - no dashboard layout
                  <Outlet />
                ) : (
                  // Protected dashboard layout
                  <KioskProtectedRoute>
                    <DashboardLayout />
                  </KioskProtectedRoute>
                )}
                {/* Toast notifications */}
                <Toaster />
              </SidebarProvider>
            </LanguageSessionProvider>
          </I18nProvider>
        </ThemeProvider>
      </AuthInitializer>
    </StoreProvider>
  )
}
